import { config } from './config';

export class ApiError extends Error {
    status: number;
    data: any;
    constructor(message: string, status: number, data: any) {
        super(message);
        this.status = status;
        this.data = data;
    }
}

// Callback for handling token expiration - will be set by AuthContext
let onTokenExpired: (() => void) | null = null;

export const setTokenExpiredCallback = (callback: () => void) => {
    onTokenExpired = callback;
};

const withTimeout = async <T>(promise: Promise<T>, ms: number): Promise<T> => {
    let timeoutId: any;
    const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Request timed out')), ms);
    });
    try {
        return await Promise.race([promise, timeoutPromise]);
    } finally {
        clearTimeout(timeoutId);
    }
};

export interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    headers?: Record<string, string>;
    body?: any;
    authToken?: string;
    // Optional: override the base URL for specific services (e.g., Besu API)
    baseUrl?: string;
}

export const apiClient = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
    // Only fall back to config.apiBaseUrl when baseUrl is strictly undefined.
    const baseInput = options.baseUrl !== undefined ? options.baseUrl : config.apiBaseUrl;
    const base = (baseInput || '').replace(/\/$/, '');
    if (!base) {
        throw new Error('API base URL is not configured. Set REACT_APP_API_BASE_URL or provide RequestOptions.baseUrl.');
    }
    const url = `${base}/${path.replace(/^\//, '')}`;
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    // Automatically add Bearer token from storage if not explicitly provided
    if (options.authToken) {
        headers['Authorization'] = `Bearer ${options.authToken}`;
    } else {
        // Prefer sessionStorage token for non-remembered sessions
        let token: string | null = null;
        try {
            token = sessionStorage.getItem('auth_token');
            if (!token) {
                // Check localStorage token only if not expired per our app-level expiry
                const expiryStr = localStorage.getItem('auth_expiry');
                const lsToken = localStorage.getItem('auth_token');
                if (lsToken && expiryStr) {
                    const expiresAt = parseInt(expiryStr, 10);
                    if (!isNaN(expiresAt) && Date.now() < expiresAt) {
                        token = lsToken;
                    } else if (!isNaN(expiresAt) && Date.now() >= expiresAt) {
                        // Clear expired local auth
                        localStorage.removeItem('auth_user');
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('auth_expiry');
                    }
                }
            }
        } catch {}
        if (token) headers['Authorization'] = `Bearer ${token}`;
    }

    const doFetch = async (): Promise<T> => {
        const res = await fetch(url, {
            method: options.method || 'GET',
            headers,
            body: options.body ? JSON.stringify(options.body) : undefined,
        });
        const contentType = res.headers.get('content-type') || '';
        const data = contentType.includes('application/json') ? await res.json() : await res.text();

        // Handle 401 Unauthorized - Token expired or invalid
        if (res.status === 401) {
            console.warn('Token expired or unauthorized. Logging out...');
            if (onTokenExpired) {
                onTokenExpired();
            }
            throw new ApiError('Unauthorized - Token expired', 401, data);
        }

        if (!res.ok) {
            throw new ApiError(`Request failed: ${res.status}`, res.status, data);
        }
        return data as T;
    };

    return withTimeout<T>(doFetch(), config.apiTimeoutMs);
};
